import { useState, useEffect, useCallback } from 'react'
import {
  getFeaturedShops,
  createFeaturedShop,
  updateFeaturedShop,
  deleteFeaturedShop,
  reorderFeaturedShops,
  type FeaturedShopSummary,
  type FeaturedShopFilters,
  type FeaturedShopCreateData,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { FeaturedShopCreateModal } from './FeaturedShopCreateModal'

const activeOptions = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
]

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      }`}
    >
      {isActive ? 'active' : 'inactive'}
    </span>
  )
}

function ShopAvatar({ name, url }: { name: string; url: string | null }) {
  const [broken, setBroken] = useState(false)
  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setBroken(true)}
        className="h-9 w-9 shrink-0 rounded-full object-cover"
      />
    )
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#503864] text-sm font-semibold text-white">
      {name.trim().charAt(0).toUpperCase() || '?'}
    </div>
  )
}

export function FeaturedShops() {
  const [featured, setFeatured] = useState<FeaturedShopSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<FeaturedShopFilters>({
    is_active: '',
    search: '',
    page: 1,
    page_size: 50,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [reordering, setReordering] = useState(false)

  const [editingCell, setEditingCell] = useState<{ id: string; field: string } | null>(null)
  const [editValue, setEditValue] = useState('')

  const canEdit = usePermission('featured_shops', 'edit')
  const canCreate = usePermission('featured_shops', 'create')
  const canDelete = usePermission('featured_shops', 'delete')
  const canReorder = usePermission('featured_shops', 'reorder')

  const reorderEnabled = canReorder

  const loadFeatured = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getFeaturedShops(filters)
      if (response.ok && response.data) {
        setFeatured(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load featured shops')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadFeatured()
  }, [loadFeatured])

  const handleFilterChange = (key: keyof FeaturedShopFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const startEditing = (id: string, field: string, currentValue: string) => {
    if (!canEdit) return
    setEditingCell({ id, field })
    setEditValue(currentValue)
  }

  const saveEdit = async () => {
    if (!editingCell) return
    const { id } = editingCell
    const response = await updateFeaturedShop(id, { note: editValue })
    if (response.ok) {
      loadFeatured()
    } else {
      setError(response.error?.message || 'Failed to update featured shop')
    }
    setEditingCell(null)
  }

  const cancelEdit = () => {
    setEditingCell(null)
  }

  const handleActiveToggle = async (id: string, isActive: boolean) => {
    const response = await updateFeaturedShop(id, { is_active: isActive })
    if (response.ok) {
      loadFeatured()
    } else {
      setError(response.error?.message || 'Failed to update featured shop')
    }
  }

  const handleDelete = async (id: string) => {
    const response = await deleteFeaturedShop(id)
    if (response.ok) {
      setDeleteConfirmId(null)
      loadFeatured()
    } else {
      setError(response.error?.message || 'Failed to remove featured shop')
    }
  }

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= featured.length) return
    const next = [...featured]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    const previous = featured
    setFeatured(next)
    setReordering(true)
    try {
      const response = await reorderFeaturedShops(next.map((f) => f.id))
      if (response.ok) {
        loadFeatured()
      } else {
        setFeatured(previous)
        setError(response.error?.message || 'Failed to reorder featured shops')
      }
    } catch {
      setFeatured(previous)
      setError('Failed to connect to API')
    } finally {
      setReordering(false)
    }
  }

  const handleCreate = async (data: FeaturedShopCreateData) => {
    const response = await createFeaturedShop(data)
    if (response.ok) {
      loadFeatured()
    } else {
      throw new Error(response.error?.message || 'Failed to feature shop')
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-white">Featured Shops</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} featured shops</p>
        </div>
        {canCreate && (
          <CustomButton variant="primary" onClick={() => setShowCreateModal(true)}>
            Feature Shop
          </CustomButton>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search shops..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.is_active || ''}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-1 focus:ring-[#503864]"
        >
          {activeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#503864] border-t-transparent" />
        </div>
      ) : featured.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-gray-400">No featured shops yet</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#3d2c4f]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#1a1520]">
                {reorderEnabled && <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Order</th>}
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Shop</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Note</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Position</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Added</th>
                {canDelete && <th className="px-3 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {featured.map((item, index) => (
                <tr key={item.id} className="border-b border-gray-100 dark:border-[#3d2c4f] hover:bg-gray-50 dark:hover:bg-[#2d2236]">
                  {reorderEnabled && (
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0 || reordering}
                          className="rounded p-1 text-gray-400 hover:text-[#7f4f9a] disabled:opacity-30 disabled:hover:text-gray-400"
                          title="Move up"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleMove(index, 1)}
                          disabled={index === featured.length - 1 || reordering}
                          className="rounded p-1 text-gray-400 hover:text-[#7f4f9a] disabled:opacity-30 disabled:hover:text-gray-400"
                          title="Move down"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                  {/* Shop */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <ShopAvatar name={item.shop_name} url={item.shop_avatar_url} />
                      <div className="min-w-0">
                        <div className="truncate font-medium text-gray-900 dark:text-white">{item.shop_name}</div>
                        {item.shop_handle && (
                          <div className="truncate text-xs text-gray-500 dark:text-gray-400">@{item.shop_handle}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  {/* Note - editable */}
                  <td className="max-w-xs px-3 py-3">
                    {editingCell?.id === item.id && editingCell?.field === 'note' ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit()
                          if (e.key === 'Escape') cancelEdit()
                        }}
                        autoFocus
                        className="w-full rounded border border-[#503864] bg-white dark:bg-[#1a1520] px-2 py-1 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      />
                    ) : (
                      <span
                        className={`block truncate text-gray-700 dark:text-gray-300 ${canEdit ? 'cursor-pointer hover:text-[#503864] dark:hover:text-[#b58fcc]' : ''}`}
                        onDoubleClick={() => startEditing(item.id, 'note', item.note || '')}
                        title={item.note || ''}
                      >
                        {item.note || '—'}
                      </span>
                    )}
                  </td>
                  {/* Position */}
                  <td className="px-3 py-3 text-gray-700 dark:text-gray-300">{item.sort_order}</td>
                  {/* Status */}
                  <td className="px-3 py-3">
                    {canEdit ? (
                      <select
                        value={item.is_active ? 'true' : 'false'}
                        onChange={(e) => handleActiveToggle(item.id, e.target.value === 'true')}
                        className="cursor-pointer rounded-full border-0 bg-transparent text-xs font-medium focus:outline-none focus:ring-1 focus:ring-[#503864]"
                      >
                        <option value="true">active</option>
                        <option value="false">inactive</option>
                      </select>
                    ) : (
                      <ActiveBadge isActive={item.is_active} />
                    )}
                  </td>
                  {/* Added */}
                  <td className="whitespace-nowrap px-3 py-3 text-gray-700 dark:text-gray-300">
                    {new Date(item.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  {/* Actions */}
                  {canDelete && (
                    <td className="px-3 py-3">
                      {deleteConfirmId === item.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-xs font-medium text-red-600 hover:text-red-800"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(item.id)}
                          className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title="Remove"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton onClick={() => handlePageChange(page - 1)} disabled={page <= 1} variant="secondary">
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </span>
          <CustomButton onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} variant="secondary">
            Next
          </CustomButton>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <FeaturedShopCreateModal onClose={() => setShowCreateModal(false)} onCreate={handleCreate} />
      )}
    </div>
  )
}
